<template>
  <div class="sp-hero-item" :class="classModifiers">
    <div class="sp-hero-item__background">
      <div v-if="video" class="sp-hero-item__background-asset">
        <video ref="videoRef" :src="video" :aria-label="title" :autoplay="active" muted loop playsinline />
      </div>

      <div v-else class="sp-hero-item__background-asset">
        <slot ref="imageSlot" name="image">
          <img :src="image" :alt="title" />
        </slot>
      </div>
      <div class="sp-hero-item__background-overlay"></div>
    </div>

    <div class="sp-hero-item__content">
      <div class="sp-hero-item__main">
        <sp-text v-if="title" tag="h2" :type="titleSize" class="sp-hero-item__title">
          <slot name="title">{{ title }}</slot>
        </sp-text>

        <div class="sp-hero-item__content-body">
          <slot />
        </div>

        <sp-hero-action v-if="actionUrl" v-bind="actionProps">{{ actionLabel }}</sp-hero-action>
      </div>

      <div class="sp-hero-item__captions">
        <slot name="captions">{{ captions }}</slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, useTemplateRef } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useExpose } from "#ce/composables/useExpose";
import { getSlotContentByTagName } from "#ce/utils/slots";
import { booleanAttribute, toBoolean } from "#ce/utils/props";

const props = defineProps({
  titleSize: {
    type: String,
    default: "h1",
  },
  title: {
    type: String,
    default: undefined,
  },
  image: {
    type: String,
    default: undefined,
  },
  video: {
    type: String,
    default: undefined,
  },
  captions: {
    type: String,
    default: undefined,
  },
  active: booleanAttribute(),
  align: {
    type: String,
    default: "start",
    validator: (value) => ["start", "center", "end"].includes(value),
  },
  theme: {
    type: String,
    default: "light",
    validator: (value) => ["dark", "light"].includes(value),
  },
  mobileTextPlacement: {
    type: String,
    default: "",
    validator: (value) => ["", "top", "bottom"].includes(value),
  },
  // Action
  actionUrl: {
    type: String,
    default: undefined,
  },
  actionLabel: {
    type: String,
    default: undefined,
  },
  actionColor: {
    type: String,
    default: undefined,
  },
  actionVariant: {
    type: String,
    default: undefined,
  },
});

const imageSlot = useTemplateRef("imageSlot");
const active = ref(toBoolean(props.active));
const videoRef = useTemplateRef("videoRef");
const actionProps = computed(() => ({
  url: props.actionUrl,
  color: props.actionColor,
  variant: props.actionVariant,
  label: props.actionLabel,
}));

const { breakpointModifiers } = useBreakpoints();
const { exposeMethods } = useExpose();

function loadDeferredImage() {
  const [image] = getSlotContentByTagName(imageSlot.value, "SP-RESPONSIVE-IMAGE");
  image?.load?.();
}

const classModifiers = computed(() => {
  const modifiers = {
    "--has-title": props.title !== undefined,
    "--has-image": props.image !== undefined,
    "--has-video": props.video !== undefined,
    "--active": active.value,
    [`--theme-${props.theme}`]: props.theme,
    [`--align-${props.align}`]: props.align,
  };

  if (props.mobileTextPlacement) {
    modifiers[`--mobile-placement-${props.mobileTextPlacement}`] = true;
  }

  return [...breakpointModifiers.value, modifiers];
});

exposeMethods({
  setActive(isActive) {
    active.value = toBoolean(isActive);

    if (!videoRef.value) {
      return;
    }

    if (active.value) {
      videoRef.value.currentTime = 0;
      videoRef.value.play();
    } else {
      videoRef.value.pause();
    }
  },
  loadImage() {
    loadDeferredImage();
  },
});
</script>

<style>
:host {
  display: block;
  height: 100%;
  width: 100%;
  position: absolute;
}
</style>

<style scoped lang="scss">
.sp-hero-item {
  --item-font: var(--sp-comp-hero-item-font, var(--sp-sys-typescale-body-short));
  --item-title-font: var(--sp-comp-hero-item-title-font, var(--sp-sys-typescale-h1-short));
  --item-title-offset: var(--sp-comp-hero-item-title-offset, calc(var(--sp-ref-scale-16) * -1));

  --item-content-font: var(--sp-comp-hero-item-content-font, var(--item-font));
  --item-content-line-height: var(--sp-comp-hero-item-content-line-height, var(--sp-sys-typescale-body-line-height));
  --item-content-gap: var(--sp-comp-hero-item-content-gap, 1rem);
  --item-overlay-dark-background-color: var(
    --sp-comp-hero-item-overlay-dark-start-background-color,
    var(--sp-sys-color-black, #000)
  );
  --item-overlay-light-background-color: var(
    --sp-comp-hero-item-overlay-light-start-background-color,
    var(--sp-sys-color-white, #fff)
  );

  --item-content-max-width: var(--sp-comp-hero-item-content-max-width, 40%);
  --item-content-body-max-width: var(--sp-comp-hero-item-content-body-max-width, 80%);

  display: flex;
  font: var(--item-font);
  justify-content: v-bind(align);
  position: relative;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity var(--transition-duration, 500ms) ease;

  &.--active {
    opacity: 1;
  }

  &.--align-start {
    --item-overlay-direction: to right;
  }

  &.--align-center {
    --item-overlay-direction: to bottom;
  }

  &.--align-end {
    --item-overlay-direction: to left;
  }

  &.--is-sm-screen {
    --item-overlay-direction: to bottom;
    --item-content-max-width: 100%;
    --item-content-body-max-width: 100%;
    --item-padding-inline: var(--item-padding-inline-sm);
    --item-padding-block: var(--item-padding-block-sm);
    --item-content-gap: var(--sp-ref-scale-16);
    --item-title-font: var(--sp-comp-hero-item-title-font-sm, var(--sp-sys-typescale-h1-small-short));
    --item-title-offset: var(--sp-comp-hero-item-title-offset-sm, calc(var(--sp-ref-scale-8) * -1));
    --item-content-font: var(--sp-comp-hero-item-content-font-sm, var(--sp-sys-typescale-body-small-short));
    --item-content-line-height: var(
      --sp-comp-hero-item-content-line-height-sm,
      var(--sp-sys-typescale-body-small-line-height)
    );

    // Mobile text placement: move title/body/CTA only; captions stay anchored at the bottom.
    &.--mobile-placement-top .sp-hero-item__content,
    &.--mobile-placement-bottom .sp-hero-item__content {
      min-height: 100%;
      box-sizing: border-box;
      padding-block-end: calc(
        var(--item-padding-block-sm-end) + var(--sp-comp-hero-item-mobile-placement-extra-end, var(--sp-ref-scale-40))
      );
    }

    &.--mobile-placement-top {
      --item-overlay-direction: to bottom;

      .sp-hero-item__captions {
        flex-grow: 0;
        margin-top: auto;
      }
    }

    &.--mobile-placement-bottom {
      --item-overlay-direction: to top;

      .sp-hero-item__content {
        // safe: overflowing content aligns to the top so a tall title is never clipped off the card
        justify-content: safe flex-end;
      }

      .sp-hero-item__captions {
        flex-grow: 0;
      }
    }
  }

  &.--is-lg-screen,
  &.--is-md-screen {
    --item-title-font: var(--sp-comp-hero-item-title-font-md, var(--sp-sys-typescale-h1-short));
    --item-padding-inline: var(--item-padding-inline-md);

    --item-padding-block-md-start: var(--sp-comp-hero-item-padding-block-md-start, var(--sp-ref-scale-64));
    --item-padding-block-md-end: calc(
      var(--item-padding-slideshow-nav-offset) + var(--sp-comp-hero-item-padding-block-md-end, var(--sp-ref-scale-80))
    );
    --item-padding-block-md: var(--item-padding-block-md-start) var(--item-padding-block-md-end);
    --item-padding-block: var(--item-padding-block-md);
  }

  &.--is-md-screen {
    --item-content-max-width: 100%;
  }

  // Video slides have a single source with no per-breakpoint crops, so on sm and
  // md screens (below the 992px md breakpoint) the full frame is shown letterboxed
  // and centered instead of cover-cropped at the sides.
  &.--is-sm-screen,
  &.--is-md-screen {
    .sp-hero-item__background video {
      object-fit: contain;
    }
  }

  &.--theme-dark {
    --item-overlay-start-background-color: var(
      --sp-comp-hero-item-theme-dark-overlay-start-background-color,
      var(--item-overlay-dark-background-color)
    );

    --item-overlay-middle-background-color: var(
      --sp-comp-hero-item-theme-dark-overlay-middle-background-color,
      var(--item-overlay-start-background-color)
    );

    --item-overlay-end-background-color: var(
      --sp-comp-hero-item-theme-dark-overlay-end-background-color,
      var(--item-overlay-light-background-color)
    );

    --item-overlay-start-alpha: var(
      --sp-comp-hero-item-theme-dark-overlay-start-alpha,
      var(--sp-comp-hero-item-background-color-alpha, 70%)
    );

    --item-overlay-end-alpha: var(
      --sp-comp-hero-item-theme-dark-overlay-end-alpha,
      var(--sp-comp-hero-item-background-color-alpha, 10%)
    );

    --item-overlay-middle-alpha: var(
      --sp-comp-hero-item-theme-dark-overlay-middle-alpha,
      var(--item-overlay-start-alpha)
    );

    --item-overlay-start-stop: var(--sp-comp-hero-item-theme-dark-overlay-start-stop, 0%);
    --item-overlay-middle-stop: var(--sp-comp-hero-item-theme-dark-overlay-middle-stop, 0%);
    --item-overlay-end-stop: var(--sp-comp-hero-item-theme-dark-overlay-end-stop, 100%);

    --item-color: var(--sp-comp-hero-item-theme-dark-text-color, var(--sp-sys-color-on-black, #fff));
  }

  &.--theme-light {
    --item-overlay-start-background-color: var(
      --sp-comp-hero-item-theme-light-overlay-start-background-color,
      var(--item-overlay-light-background-color)
    );

    --item-overlay-middle-background-color: var(
      --sp-comp-hero-item-theme-light-overlay-middle-background-color,
      var(--item-overlay-start-background-color)
    );

    --item-overlay-end-background-color: var(
      --sp-comp-hero-item-theme-light-overlay-end-background-color,
      var(--item-overlay-dark-background-color)
    );

    --item-overlay-start-alpha: var(
      --sp-comp-hero-item-theme-light-overlay-start-alpha,
      var(--sp-comp-hero-item-background-color-alpha, 70%)
    );

    --item-overlay-end-alpha: var(
      --sp-comp-hero-item-theme-light-overlay-end-alpha,
      var(--sp-comp-hero-item-background-color-alpha, 10%)
    );

    --item-overlay-middle-alpha: var(
      --sp-comp-hero-item-theme-light-overlay-middle-alpha,
      var(--item-overlay-start-alpha)
    );

    --item-overlay-start-stop: var(--sp-comp-hero-item-theme-light-overlay-start-stop, 0%);
    --item-overlay-middle-stop: var(--sp-comp-hero-item-theme-light-overlay-middle-stop, 0%);
    --item-overlay-end-stop: var(--sp-comp-hero-item-theme-light-overlay-end-stop, 100%);

    --item-color: var(--sp-comp-hero-item-theme-light-text-color, var(--sp-sys-color-on-white, #000));
  }

  ::slotted(sp-responsive-image) {
    --sp-comp-responsive-image-border-radius: var(--item-border-radius);
    --sp-comp-responsive-image-width: 100%;
    --sp-comp-responsive-image-height: 100%;
    display: block;
    width: 100%;
    height: 100%;
  }

  &__title {
    font: var(--item-title-font);
    margin-block: 0;
    margin-block-end: var(--item-title-offset);
    word-break: normal;
    overflow-wrap: normal;
  }

  &__background {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    border-radius: var(--item-border-radius);
    width: var(--item-background-width);
    margin-inline: var(--item-background-margin-inline);

    &-overlay {
      --item-overlay-start-gradient: color-mix(
        in srgb,
        var(--item-overlay-start-background-color) var(--item-overlay-start-alpha),
        transparent
      );
      --item-overlay-middle-gradient: color-mix(
        in srgb,
        var(--item-overlay-middle-background-color) var(--item-overlay-middle-alpha),
        transparent
      );
      --item-overlay-end-gradient: color-mix(
        in srgb,
        var(--item-overlay-end-background-color) var(--item-overlay-end-alpha),
        transparent
      );

      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      z-index: 1;
      border-radius: var(--item-border-radius);
      background: linear-gradient(
        var(--item-overlay-direction),
        var(--item-overlay-start-gradient) var(--item-overlay-start-stop),
        var(--item-overlay-middle-gradient) var(--item-overlay-middle-stop),
        var(--item-overlay-end-gradient) var(--item-overlay-end-stop)
      );
    }

    &-asset {
      width: 100%;
      height: 100%;
    }

    video,
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__content {
    --sp-comp-layout-row-color-link: var(--item-color);
    --sp-comp-text-color: var(--item-color);

    position: relative;
    display: flex;
    flex-direction: column;
    max-width: var(--item-content-max-width);
    gap: var(--item-content-gap);
    z-index: 2;
    padding-block: var(--item-padding-block);
    padding-inline: var(--item-padding-inline);
    color: var(--item-color);
    text-shadow: 0 var(--sp-ref-scale-2) var(--sp-ref-scale-4) rgba(0, 0, 0, 0.5);

    sp-hero-action[variant="flat"],
    sp-hero-action[variant="outlined"] {
      text-shadow: none;
    }

    &-body {
      --sp-comp-wysiwyg-content-font: var(--item-content-font);
      --sp-comp-wysiwyg-content-line-height: var(--item-content-line-height);
      display: flex;
      max-width: var(--item-content-body-max-width);
      flex-direction: column;
      align-self: v-bind(align);
      font: var(--item-content-font);
    }

    .sp-hero-item__main {
      display: flex;
      flex-direction: column;
      gap: var(--item-content-gap);
    }

    .sp-hero-item__captions {
      flex-grow: 1;
      display: flex;
      align-items: flex-end;
    }
  }
}
</style>
